import type { HTMLAttributes, ReactNode } from 'react';
import { BinGlyph } from './BinGlyph';

export type DsBin = 'green' | 'purple' | 'blue' | 'orange' | 'pink';

const PILL: Record<DsBin, { bg: string; text: string }> = {
  green: { bg: 'var(--green-bg)', text: 'var(--green-text)' },
  purple: { bg: 'var(--purple-bg)', text: 'var(--purple-text)' },
  blue: { bg: 'var(--blue-bg)', text: 'var(--blue-text)' },
  orange: { bg: 'var(--orange-bg)', text: 'var(--orange-text)' },
  pink: { bg: 'var(--pink-bg)', text: 'var(--pink-text)' },
};

export interface BinPillProps extends HTMLAttributes<HTMLSpanElement> {
  bin?: DsBin;
  /** Show the bin's shape marker. Keep it on unless the pill is purely decorative. */
  dot?: boolean;
  children?: ReactNode;
}

/**
 * CWY BinPill — a compact status pill in a mastery-bin colour (shape + label).
 *
 * The marker is a per-bin SHAPE rather than a plain dot, so the bin survives
 * colour-vision deficiency and greyscale printing. See BinGlyph.
 */
export function BinPill({ bin = 'green', children, dot = true, style, ...rest }: BinPillProps) {
  const c = PILL[bin];
  const label = children ?? bin[0].toUpperCase() + bin.slice(1);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        background: c.bg,
        color: c.text,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-2xs)',
        fontWeight: 600,
        lineHeight: 1.6,
        ...style,
      }}
      {...rest}
    >
      {dot && <BinGlyph bin={bin} size={9} title={null} />}
      {label}
    </span>
  );
}
